/**
 * Amazon brand string overrides (APM terminology), per language.
 * Shape mirrors the base language files; only differing keys are present.
 * Placeholders carry the format example; tooltips carry the explanation.
 * Non-English strings are machine-translated — flagged for native review.
 */
const buildTranslation = (s) => ({
  translation: {
    components: {
      recordFields: {
        ROUTE: s.routeName,
        ROUTE_LOCATION: s.routeLocation,
        ROUTE_EQUIPMENT_NAME: s.routeEquipmentId,
      },
    },
    views: {
      companies: {
        routes: {
          commonUsedWords: {
            name: s.equipmentId,
            equipmentNameIsRequired: s.equipmentIdIsRequired,
          },
          index: {
            routePlans: s.routeName,
            routeEquipment: s.routeEquipmentId,
            equipment: s.equipmentId,
          },
          addRouteModal: {
            routeName: s.routeName,
            routeLocation: s.routeLocation,
            equipmentName: s.equipmentId,
            routeNameIsRequired: s.routeNameIsRequired,
            routeLocationIsRequired: s.routeLocationIsRequired,
            routeNameTooltip: s.ttRouteName,
            locationTooltip: s.ttRouteLocation,
            equipmentNameTooltip: s.ttEquipmentName,
            equipmentDescriptionTooltip: s.ttEquipmentDescription,
            placeholders: {
              routeName: s.phRouteName,
              routeLocation: s.phRouteLocation,
              equipmentName: s.phEquipmentName,
              equipmentDescription: s.phEquipmentDescription,
            },
          },
        },
      },
    },
  },
});

export default {
  en: buildTranslation({
    routeName: 'APM Air Leak Route Name',
    routeLocation: 'Route Location Area',
    equipmentId: 'APM Equipment ID',
    routeEquipmentId: 'Route APM Equipment ID',
    routeNameIsRequired: 'APM Air Leak Route Name is required',
    routeLocationIsRequired: 'Route Location Area is required',
    equipmentIdIsRequired: 'APM Equipment ID is required',
    ttRouteName: 'What is the APM Air Leak Route Name',
    ttRouteLocation: 'Explain where this process path is in the facility',
    ttEquipmentName: 'Full APM Equipment ID of asset',
    ttEquipmentDescription: 'Description of asset',
    phRouteName: 'e.g. CBM.ULTS.01',
    phRouteLocation: 'e.g. AFE1 Sorter Lane1',
    phEquipmentName: 'e.g. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'e.g. Compressor, MHE conveyor, Pneumatic fittings',
  }),
  ch: buildTranslation({
    routeName: 'APM 空气泄漏路线名称',
    routeLocation: '路线位置区域',
    equipmentId: 'APM 设备 ID',
    routeEquipmentId: '路线 APM 设备 ID',
    routeNameIsRequired: 'APM 空气泄漏路线名称为必填项',
    routeLocationIsRequired: '路线位置区域为必填项',
    equipmentIdIsRequired: 'APM 设备 ID 为必填项',
    ttRouteName: '什么是 APM 空气泄漏路线名称',
    ttRouteLocation: '说明该流程路径在设施中的位置',
    ttEquipmentName: '资产的完整 APM 设备 ID',
    ttEquipmentDescription: '资产描述',
    phRouteName: '例如：CBM.ULTS.01',
    phRouteLocation: '例如：AFE1 Sorter Lane1',
    phEquipmentName: '例如：MRG.RCV.IF02.101550',
    phEquipmentDescription: '例如：压缩机、MHE 输送机、气动接头',
  }),
  es: buildTranslation({
    routeName: 'Nombre de la ruta de fugas de aire APM',
    routeLocation: 'Área de ubicación de la ruta',
    equipmentId: 'ID de equipo APM',
    routeEquipmentId: 'ID de equipo APM de la ruta',
    routeNameIsRequired: 'El nombre de la ruta de fugas de aire APM es obligatorio',
    routeLocationIsRequired: 'El área de ubicación de la ruta es obligatoria',
    equipmentIdIsRequired: 'El ID de equipo APM es obligatorio',
    ttRouteName: '¿Cuál es el nombre de la ruta de fugas de aire APM?',
    ttRouteLocation: 'Explique dónde está esta trayectoria de proceso en la instalación',
    ttEquipmentName: 'ID de equipo APM completo del activo',
    ttEquipmentDescription: 'Descripción del activo',
    phRouteName: 'p. ej., CBM.ULTS.01',
    phRouteLocation: 'p. ej., AFE1 Sorter Lane1',
    phEquipmentName: 'p. ej., MRG.RCV.IF02.101550',
    phEquipmentDescription: 'p. ej., compresor, transportador MHE, accesorios neumáticos',
  }),
  fr: buildTranslation({
    routeName: "Nom du circuit de fuites d'air APM",
    routeLocation: "Zone d'emplacement du circuit",
    equipmentId: "ID d'équipement APM",
    routeEquipmentId: "ID d'équipement APM du circuit",
    routeNameIsRequired: "Le nom du circuit de fuites d'air APM est requis",
    routeLocationIsRequired: "La zone d'emplacement du circuit est requise",
    equipmentIdIsRequired: "L'ID d'équipement APM est requis",
    ttRouteName: "Quel est le nom du circuit de fuites d'air APM ?",
    ttRouteLocation: "Expliquez où se trouve ce chemin de processus dans l'installation",
    ttEquipmentName: "ID d'équipement APM complet de l'actif",
    ttEquipmentDescription: "Description de l'actif",
    phRouteName: 'p. ex. CBM.ULTS.01',
    phRouteLocation: 'p. ex. AFE1 Sorter Lane1',
    phEquipmentName: 'p. ex. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'p. ex. compresseur, convoyeur MHE, raccords pneumatiques',
  }),
  gr: buildTranslation({
    routeName: 'APM-Druckluftleckage-Routenname',
    routeLocation: 'Routenstandortbereich',
    equipmentId: 'APM-Geräte-ID',
    routeEquipmentId: 'Routen-APM-Geräte-ID',
    routeNameIsRequired: 'APM-Druckluftleckage-Routenname ist erforderlich',
    routeLocationIsRequired: 'Routenstandortbereich ist erforderlich',
    equipmentIdIsRequired: 'APM-Geräte-ID ist erforderlich',
    ttRouteName: 'Wie lautet der APM-Druckluftleckage-Routenname?',
    ttRouteLocation: 'Beschreiben Sie, wo sich dieser Prozesspfad in der Anlage befindet',
    ttEquipmentName: 'Vollständige APM-Geräte-ID des Assets',
    ttEquipmentDescription: 'Beschreibung des Assets',
    phRouteName: 'z. B. CBM.ULTS.01',
    phRouteLocation: 'z. B. AFE1 Sorter Lane1',
    phEquipmentName: 'z. B. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'z. B. Kompressor, MHE-Förderband, Pneumatikanschlüsse',
  }),
  nor: buildTranslation({
    routeName: 'APM luftlekkasje-rutenavn',
    routeLocation: 'Ruteplasseringsområde',
    equipmentId: 'APM utstyrs-ID',
    routeEquipmentId: 'Rute-APM utstyrs-ID',
    routeNameIsRequired: 'APM luftlekkasje-rutenavn er påkrevd',
    routeLocationIsRequired: 'Ruteplasseringsområde er påkrevd',
    equipmentIdIsRequired: 'APM utstyrs-ID er påkrevd',
    ttRouteName: 'Hva er APM luftlekkasje-rutenavnet?',
    ttRouteLocation: 'Forklar hvor denne prosessbanen er i anlegget',
    ttEquipmentName: 'Fullstendig APM utstyrs-ID for ressursen',
    ttEquipmentDescription: 'Beskrivelse av ressursen',
    phRouteName: 'f.eks. CBM.ULTS.01',
    phRouteLocation: 'f.eks. AFE1 Sorter Lane1',
    phEquipmentName: 'f.eks. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'f.eks. kompressor, MHE-transportbånd, pneumatiske koblinger',
  }),
  itly: buildTranslation({
    routeName: "Nome del percorso perdite d'aria APM",
    routeLocation: 'Area di ubicazione del percorso',
    equipmentId: 'ID attrezzatura APM',
    routeEquipmentId: 'ID attrezzatura APM del percorso',
    routeNameIsRequired: "Il nome del percorso perdite d'aria APM è obbligatorio",
    routeLocationIsRequired: "L'area di ubicazione del percorso è obbligatoria",
    equipmentIdIsRequired: "L'ID attrezzatura APM è obbligatorio",
    ttRouteName: "Qual è il nome del percorso perdite d'aria APM?",
    ttRouteLocation: 'Spiega dove si trova questo percorso di processo nello stabilimento',
    ttEquipmentName: "ID attrezzatura APM completo dell'asset",
    ttEquipmentDescription: "Descrizione dell'asset",
    phRouteName: 'es. CBM.ULTS.01',
    phRouteLocation: 'es. AFE1 Sorter Lane1',
    phEquipmentName: 'es. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'es. compressore, trasportatore MHE, raccordi pneumatici',
  }),
  tur: buildTranslation({
    routeName: 'APM Hava kaçağı Rota adı',
    routeLocation: 'Rota Konum Alanı',
    equipmentId: 'APM Ekipman Kimliği',
    routeEquipmentId: 'Rota APM Ekipman Kimliği',
    routeNameIsRequired: 'APM Hava kaçağı Rota adı gereklidir',
    routeLocationIsRequired: 'Rota Konum Alanı gereklidir',
    equipmentIdIsRequired: 'APM Ekipman Kimliği gereklidir',
    ttRouteName: 'APM Hava kaçağı Rota adı nedir?',
    ttRouteLocation: 'Bu proses yolunun tesiste nerede olduğunu açıklayın',
    ttEquipmentName: 'Varlığın tam APM Ekipman Kimliği',
    ttEquipmentDescription: 'Varlık açıklaması',
    phRouteName: 'örn. CBM.ULTS.01',
    phRouteLocation: 'örn. AFE1 Sorter Lane1',
    phEquipmentName: 'örn. MRG.RCV.IF02.101550',
    phEquipmentDescription: 'örn. kompresör, MHE konveyör, pnömatik bağlantılar',
  }),
  ja: buildTranslation({
    routeName: 'APM エア漏れルート名',
    routeLocation: 'ルート設置エリア',
    equipmentId: 'APM 機器 ID',
    routeEquipmentId: 'ルート APM 機器 ID',
    routeNameIsRequired: 'APM エア漏れルート名は必須です',
    routeLocationIsRequired: 'ルート設置エリアは必須です',
    equipmentIdIsRequired: 'APM 機器 ID は必須です',
    ttRouteName: 'APM エア漏れルート名とは',
    ttRouteLocation: 'このプロセス経路が施設内のどこにあるか説明してください',
    ttEquipmentName: '資産の完全な APM 機器 ID',
    ttEquipmentDescription: '資産の説明',
    phRouteName: '例：CBM.ULTS.01',
    phRouteLocation: '例：AFE1 Sorter Lane1',
    phEquipmentName: '例：MRG.RCV.IF02.101550',
    phEquipmentDescription: '例：コンプレッサー、MHE コンベア、空圧継手',
  }),
  pt: buildTranslation({
    routeName: 'Nome da rota de vazamento de ar APM',
    routeLocation: 'Área de localização da rota',
    equipmentId: 'ID do equipamento APM',
    routeEquipmentId: 'ID do equipamento APM da rota',
    routeNameIsRequired: 'O nome da rota de vazamento de ar APM é obrigatório',
    routeLocationIsRequired: 'A área de localização da rota é obrigatória',
    equipmentIdIsRequired: 'O ID do equipamento APM é obrigatório',
    ttRouteName: 'Qual é o nome da rota de vazamento de ar APM?',
    ttRouteLocation: 'Explique onde este caminho de processo está na instalação',
    ttEquipmentName: 'ID do equipamento APM completo do ativo',
    ttEquipmentDescription: 'Descrição do ativo',
    phRouteName: 'ex.: CBM.ULTS.01',
    phRouteLocation: 'ex.: AFE1 Sorter Lane1',
    phEquipmentName: 'ex.: MRG.RCV.IF02.101550',
    phEquipmentDescription: 'ex.: compressor, transportador MHE, conexões pneumáticas',
  }),
  kr: buildTranslation({
    routeName: 'APM 공기 누출 경로 이름',
    routeLocation: '경로 위치 구역',
    equipmentId: 'APM 장비 ID',
    routeEquipmentId: '경로 APM 장비 ID',
    routeNameIsRequired: 'APM 공기 누출 경로 이름은 필수입니다',
    routeLocationIsRequired: '경로 위치 구역은 필수입니다',
    equipmentIdIsRequired: 'APM 장비 ID는 필수입니다',
    ttRouteName: 'APM 공기 누출 경로 이름은 무엇인가요?',
    ttRouteLocation: '이 공정 경로가 시설 내 어디에 있는지 설명하세요',
    ttEquipmentName: '자산의 전체 APM 장비 ID',
    ttEquipmentDescription: '자산 설명',
    phRouteName: '예: CBM.ULTS.01',
    phRouteLocation: '예: AFE1 Sorter Lane1',
    phEquipmentName: '예: MRG.RCV.IF02.101550',
    phEquipmentDescription: '예: 압축기, MHE 컨베이어, 공압 피팅',
  }),
};
